import React, { Fragment, useEffect, useRef, useState } from 'react';
import { BiFilterAlt, BiSortDown, BiSortUp } from 'react-icons/bi';
import ReactPaginate from 'react-paginate';
import ListOfBookings from './ListOfBookings';
import SpecificBookingDetails from './SpecificBookingDetails';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ToolTip from '../../../common/ToolTip';
import getBookingList from '../../../api/hospitality/Booking';
import Skeleton from '../common/Skeleton';
import SlidedScreen from '../../../common/SlidedScreen';
import { ImSpinner9 } from 'react-icons/im';

const Booking = () => {
  const companyUuid = sessionStorage.getItem('companyUuid');

  const itemsPerPage = 10;
  const [bookings, setBookings] = useState<any>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showLoading, setShowLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [bookingInfo, setBookingInfo] = useState<any>();
  const [selectedFilter, setSelectedFilter] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [resetData, setResetData] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const lastPageRef = useRef(currentPage);
  const [scrollList, setScrollList] = useState(false);

  const pageCount = Math.ceil(totalItems / itemsPerPage);

  const handleFetchBookings = async (
    type: string,
    page?: number,
    reset?: boolean,
    sort?: string
  ) => {
    if (isFetching || !hasMore) {
      return;
    }
    setIsFetching(true);
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
    try {
      setShowTooltip(false);
      if (!scrollList) {
        setShowLoading(true);
      }
      setSelectedFilter(type);
      const response = await getBookingList(
        companyUuid ? companyUuid : '',
        type,
        page || currentPage,
        itemsPerPage,
        sort || sortOrder
      );

      if (response.payload.bookingRoomDetails.length > 0) {
        const list =
          resetData || reset
            ? response.payload.bookingRoomDetails
            : [...bookings, ...response.payload.bookingRoomDetails];
        const ind = bookingInfo ? list.findIndex((ele: any) => ele.uuid === bookingInfo.uuid) : 0;
        setShowDetails(false);
        setBookings(list);
        setBookingInfo(list[ind === -1 ? 0 : ind] || '');
        setTotalItems(response.payload.count);
      } else {
        setHasMore(false);
      }
      // setTotalItems(response.payload.count);
      // setBookingInfo(response.payload.bookingRoomDetails[0]);
      // setBookings(response.payload.bookingRoomDetails);
    } catch (error) {
      console.error('Error fetching Booking:', error);
    } finally {
      setShowLoading(false);
      setIsFetching(false);
      setResetData(false);
      setScrollList(false);
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      }, 100);
    }
  };

  const handlePageClick = (event: { selected: number }) => {
    setCurrentPage(event.selected + 1);
  };

  useEffect(() => {
    lastPageRef.current = currentPage;
    handleFetchBookings(selectedFilter);
  }, [currentPage]);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current || isFetching || !hasMore) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        if (bookings.length < totalItems && lastPageRef.current === currentPage) {
          setScrollList(true);
          lastPageRef.current += 1;
          setCurrentPage((prev) => prev + 1);
        }
      }
    };

    const scrollableDiv = scrollContainerRef.current;
    if (scrollableDiv) {
      scrollableDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isFetching, hasMore]);

  const handleSort = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    setResetData(true);
    if (currentPage === 1) {
      const sort = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(sort);
      handleFetchBookings(selectedFilter, 1, true, sort);
    } else {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      setCurrentPage(1);
    }
  };

  return (
    <>
      <div className="bg-[#1E1E1E] h-screen py-0 w-full md:px-2 overflow-y-auto xl:overflow-hidden relative">
        <div className="flex justify-between items-center mb-2.5 px-2">
          <div className={`flex md:w-[40%] w-full justify-between items-center`}>
            <label className="text-xs md:text-base text-white font-medium">Bookings</label>
            {bookings.length > 0 && (
              <div className="justify-end hidden xl:flex">
                <div onClick={handleSort} className=" text-white font-bold px-2 rounded">
                  {sortOrder === 'asc' ? (
                    <BiSortDown className="cursor-pointer w-[30px] h-[30px]" color={'#006BEA'} />
                  ) : (
                    <BiSortUp className="cursor-pointer w-[30px] h-[30px]" color={'#006BEA'} />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end items-center gap-2.5 md:w-[60%] w-full">
            {bookings.length > 0 && (
              <div className="flex xl:hidden">
                <div onClick={handleSort} className=" text-white font-bold px-2 rounded">
                  {sortOrder === 'asc' ? (
                    <BiSortDown className="cursor-pointer w-[30px] h-[30px]" color={'#006BEA'} />
                  ) : (
                    <BiSortUp className="cursor-pointer w-[30px] h-[30px]" color={'#006BEA'} />
                  )}
                </div>
              </div>
            )}
            {selectedFilter && (
              <button
                className="bg-[#006BEA] text-[#FFFFFF] rounded-[6px] p-[5px] lg:p-[6px] xl:p-[10px] font-semibold text-xs md:text-sm"
                onClick={() => handleFetchBookings('', 1, true)}
              >
                Clear Filter
              </button>
            )}
            <div className="relative">
              <BiFilterAlt
                className="cursor-pointer w-[30px] h-[30px]"
                color={'#006BEA'}
                onClick={() => setShowTooltip(true)}
              />
              <ToolTip
                width={'md:w-[200px] lg:w-[200px] w-[150px]'}
                height={'h-[110px]'}
                isVisible={showTooltip}
                onClose={() => setShowTooltip(false)}
              >
                <ul className="flex flex-col text-[#006BEA] font-bold text-sm justify-center items-center  mt-1 cursor-pointer">
                  <li
                    className="hover:bg-gray-100 md:px-[62px] px-10 py-[6px] rounded-sm"
                    onClick={() => handleFetchBookings('booking_room', 1, true)}
                  >
                    Room
                  </li>
                  <li
                    className="hover:bg-gray-100 md:px-[62px] px-10 py-[6px] rounded-sm"
                    onClick={() => handleFetchBookings('booking_restaurant_table', 1, true)}
                  >
                    Resturant
                  </li>
                  <li
                    className="hover:bg-gray-100 md:px-[62px] px-10 py-[6px] rounded-sm"
                    onClick={() => handleFetchBookings('booking_events', 1, true)}
                  >
                    Event
                  </li>
                </ul>
              </ToolTip>
            </div>
          </div>
        </div>
        {showLoading ? (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div className="loader border-t-4 border-[#006BEA] rounded-full w-12 h-12 animate-spin"></div>
            </div>
            <Skeleton />
          </>
        ) : (
          <Fragment>
            {bookings.length > 0 ? (
              <div className="bg-[#1E1E1E] w-full overflow-y-auto xl:overflow-hidden">
                <div className="flex flex-col xl:flex-row gap-2.5 xl:h-[calc(100vh-180px)] pb-2 xl:pb-0">
                  <div
                    ref={scrollContainerRef}
                    className="w-auto md:w-full lg:w-full xl:w-[40%] overflow-y-auto xl:h-[calc(100vh-180px)] max-h-[80vh]"
                  >
                    <ListOfBookings
                      bookings={bookings || []}
                      bookingInfo={bookingInfo}
                      setBookingInfo={(e: any) => { setBookingInfo(e); setShowDetails(true); }}
                    />
                  </div>

                  <div className="w-[96%]  md:w-full xl:w-[58.5%] bg-[#343434] rounded-lg p-2 mt-2 ml-2 md:ml-0 hidden xl:block">
                    <SpecificBookingDetails tickets={bookings} bookingInfo={bookingInfo} />
                  </div>
                  <SlidedScreen showDetails={showDetails} onClick={() => setShowDetails(false)} >
                    <SpecificBookingDetails tickets={bookings} bookingInfo={bookingInfo} />
                  </SlidedScreen>
                </div>

                {isFetching && scrollList && (
                  <div className="flex justify-center lg:mt-3 md:gap-0 gap[-4px] xl:mb-1.5 w-full xl:w-[40%] mb-5 text-sm">
                    <div className='mr-2'>
                      <ImSpinner9 className="h-5 w-5 text-[#006BEA] animate-spin" />
                    </div>
                    Loading more data...
                  </div>
                )}

                {/* {bookings.length > 0 && (
                  <div className="flex justify-center lg:mt-4 md:gap-0 gap[-4px] xl:mb-1.5 w-full xl:w-[40%] mb-36 text-sm">
                    <ReactPaginate
                      previousLabel={
                        <div className="flex items-center justify-center md:w-6 md:h-5 w-5 h-4 border rounded border-white text-white">
                          <FaChevronLeft />
                        </div>
                      }
                      nextLabel={
                        <div className="flex items-center justify-center md:w-6 md:h-5 w-5 h-4 border rounded border-white text-white">
                          <FaChevronRight />
                        </div>
                      }
                      pageCount={pageCount}
                      onPageChange={handlePageClick}
                      forcePage={currentPage - 1}
                      containerClassName="pagination flex space-x-6 mb-4"
                      pageLinkClassName="cursor-pointer"
                      activeLinkClassName="text-blue-500 font-bold"
                      disabledClassName="opacity-50 cursor-not-allowed"
                      previousClassName={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}
                      nextClassName={
                        currentPage === pageCount ? 'opacity-50 cursor-not-allowed' : ''
                      }
                    />
                  </div>
                )} */}
              </div>
            ) : (
              <div
                className="flex content-center justify-center items-center h-4/6 overflow-y-hidden w-full text-gray-300 text-[16px] font-normal
              select-none"
              >
                No records found
              </div>
            )}
          </Fragment>
        )}
      </div>
    </>
  );
};

export default Booking;
